/**
 * Session Storage Manager for Sprite Tool
 * Handles saving and restoring spritesheet + atlas data using localStorage
 */

import { logger } from '../utils/logger.js';
import { STORAGE_CONFIG } from '../config/AppConfig.js';

export class SessionStorage {
    constructor() {
        this.storageKey = STORAGE_CONFIG.storageKey;
        this.maxAge = STORAGE_CONFIG.maxAge;
    }

    /**
     * Check if there's saved data and show restore prompt if found
     * @param {Function} onRestore - Callback when user chooses to restore
     * @param {Function} onStartFresh - Callback when user chooses to start fresh
     */
    checkForSavedData(onRestore, onStartFresh) {
        try {
            const savedData = localStorage.getItem(this.storageKey);
            if (savedData) {
                const data = JSON.parse(savedData);
                if (data.spritesheet && data.timestamp) {
                    const daysOld = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
                    if (daysOld < this.maxAge) {
                        this.showRestorePrompt(data, onRestore, onStartFresh);
                        return true;
                    }
                }
            }
        } catch (error) {
            logger.error('[SESSION STORAGE] Error checking saved data:', error);
        }
        
        this.clearSavedData();
        return false;
    }

    /**
     * Show restore prompt dialog
     * @param {Object} savedData - The saved data object
     * @param {Function} onRestore - Callback for restore action
     * @param {Function} onStartFresh - Callback for start fresh action
     */
    showRestorePrompt(savedData, onRestore, onStartFresh) {
        // Create restore prompt overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            backdrop-filter: blur(5px);
        `;
        
        const dialog = document.createElement('div');
        dialog.style.cssText = `
            background: rgba(0, 0, 0, 0.95);
            border: 2px solid #4CAF50;
            border-radius: 15px;
            padding: 30px;
            max-width: 500px;
            text-align: center;
            color: white;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        `;
        
        const age = Math.round((Date.now() - savedData.timestamp) / (1000 * 60 * 60));
        const ageText = age < 24 ? `${age} hours ago` : `${Math.round(age / 24)} days ago`;
        
        dialog.innerHTML = `
            <h3 style="color: #ff9800; margin-bottom: 20px;">🔄 Restore Previous Session?</h3>
            <p style="margin-bottom: 15px;">Found saved spritesheet data from ${ageText}</p>
            <div style="background: rgba(255, 255, 255, 0.05); padding: 15px; border-radius: 8px; margin: 15px 0; font-family: 'Courier New', monospace; font-size: 12px;">
                <strong>Spritesheet:</strong> ${savedData.spritesheet.name}<br>
                <strong>Size:</strong> ${savedData.spritesheet.width}×${savedData.spritesheet.height}px<br>
                ${savedData.atlas ? `<strong>Atlas:</strong> ${Object.keys(savedData.atlas.frames || {}).length} frames` : '<strong>Atlas:</strong> None'}
            </div>
            <div style="display: flex; gap: 15px; justify-content: center; margin-top: 20px;">
                <button id="restoreBtn" style="
                    background: linear-gradient(145deg, #4CAF50, #45a049);
                    border: none;
                    border-radius: 8px;
                    color: white;
                    padding: 12px 24px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                ">🔄 Restore Session</button>
                <button id="freshBtn" style="
                    background: linear-gradient(145deg, #666, #555);
                    border: none;
                    border-radius: 8px;
                    color: white;
                    padding: 12px 24px;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                ">🗑️ Start Fresh</button>
            </div>
        `;
        
        overlay.appendChild(dialog);
        document.body.appendChild(overlay);
        
        // Add event listeners
        document.getElementById('restoreBtn').addEventListener('click', () => {
            if (onRestore) onRestore(savedData);
            document.body.removeChild(overlay);
        });
        
        document.getElementById('freshBtn').addEventListener('click', () => {
            this.clearSavedData();
            if (onStartFresh) onStartFresh();
            document.body.removeChild(overlay);
        });
        
        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                this.clearSavedData();
                if (onStartFresh) onStartFresh();
                document.body.removeChild(overlay);
            }
        });
        
        // Add hover effects
        const restoreBtn = document.getElementById('restoreBtn');
        const freshBtn = document.getElementById('freshBtn');
        
        restoreBtn.addEventListener('mouseenter', () => {
            restoreBtn.style.background = 'linear-gradient(145deg, #45a049, #4CAF50)';
        });
        restoreBtn.addEventListener('mouseleave', () => {
            restoreBtn.style.background = 'linear-gradient(145deg, #4CAF50, #45a049)';
        });
        
        freshBtn.addEventListener('mouseenter', () => {
            freshBtn.style.background = 'linear-gradient(145deg, #777, #666)';
        });
        freshBtn.addEventListener('mouseleave', () => {
            freshBtn.style.background = 'linear-gradient(145deg, #666, #555)';
        });
    }

    /**
     * Save spritesheet and atlas data to localStorage
     * @param {Object} spritesheetData - Image data with file info
     * @param {Object} atlasData - Optional atlas JSON data
     */
    saveToLocalStorage(spritesheetData, atlasData = null) {
        try {
            const dataToSave = {
                timestamp: Date.now(),
                spritesheet: {
                    name: spritesheetData.file.name,
                    size: spritesheetData.file.size,
                    width: spritesheetData.image.width,
                    height: spritesheetData.image.height,
                    dataUrl: spritesheetData.image.src
                }
            };
            
            if (atlasData) {
                dataToSave.atlas = atlasData;
            }
            
            localStorage.setItem(this.storageKey, JSON.stringify(dataToSave));
return true;
        } catch (error) {
            logger.error('[SESSION STORAGE] Error saving to localStorage:', error);

            // Handle quota exceeded error
            if (error.name === 'QuotaExceededError') {
                logger.warn('[SESSION STORAGE] Storage quota exceeded, clearing old data');
                this.clearSavedData();
                return false;
            }
            
            return false;
        }
    }

    /**
     * Load saved data from localStorage
     * @returns {Object|null} Saved data or null if none found
     */
    loadFromLocalStorage() {
        try {
            const savedData = localStorage.getItem(this.storageKey);
            if (savedData) {
                const data = JSON.parse(savedData);
                if (data.spritesheet && data.timestamp) {
                    const daysOld = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
                    if (daysOld < this.maxAge) {
                        return data;
                    }
                }
            }
        } catch (error) {
            logger.error('[SESSION STORAGE] Error loading from localStorage:', error);
        }
        
        return null;
    }

    /**
     * Clear all saved data from localStorage
     */
    clearSavedData() {
        try {
            localStorage.removeItem(this.storageKey);
        } catch (error) {
            logger.error('[SESSION STORAGE] Error clearing saved data:', error);
        }
    }

    /**
     * Get info about saved data without loading it
     * @returns {Object|null} Data info or null
     */
    getSavedDataInfo() {
        try {
            const savedData = localStorage.getItem(this.storageKey);
            if (savedData) {
                const data = JSON.parse(savedData);
                if (data.spritesheet && data.timestamp) {
                    const daysOld = (Date.now() - data.timestamp) / (1000 * 60 * 60 * 24);
                    if (daysOld < this.maxAge) {
                        return {
                            exists: true,
                            age: daysOld,
                            name: data.spritesheet.name,
                            size: data.spritesheet.size,
                            dimensions: `${data.spritesheet.width}×${data.spritesheet.height}`,
                            hasAtlas: !!data.atlas,
                            frameCount: data.atlas ? Object.keys(data.atlas.frames || {}).length : 0
                        };
                    }
                }
            }
        } catch (error) {
            logger.error('[SESSION STORAGE] Error getting data info:', error);
        }
        
        return { exists: false };
    }

    /**
     * Check if localStorage is available
     * @returns {boolean} True if localStorage is available
     */
    isStorageAvailable() {
        try {
            const test = 'localStorage-test';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Get storage usage information
     * @returns {Object} Storage usage stats
     */
    getStorageStats() {
        if (!this.isStorageAvailable()) {
            return { available: false };
        }

        try {
            const data = localStorage.getItem(this.storageKey);
            const size = data ? new Blob([data]).size : 0;
            const sizeKB = Math.round(size / 1024);
            
            return {
                available: true,
                size: size,
                sizeKB: sizeKB,
                sizeMB: Math.round(sizeKB / 1024 * 100) / 100,
                hasData: !!data
            };
        } catch (error) {
            return { available: true, error: error.message };
        }
    }
}

// Export singleton instance
export const sessionStorage = new SessionStorage();